// kfe-l2b-drag-area.js — drag-and-drop tile grid for L2B Training and Testing.
//
// PORT TARGET:
//   • iEnglish/Classes/KFEModules/Learning/LanguageBody/KFEContentsView.m
//     — pan-driven tile grid with single-drag policy (setDragViewIsCanDrag)
//   • iEnglish/Classes/KFEModules/Learning/LanguageBody/KFETargetsView.m
//     — drop-target hit test (CGRectContainsPoint → didDragImageViewEnd)
//   • Unity port L2BExerciseScreen.cs (same flow in C#)
//
// L2B is drag-the-correct-tile-into-the-target-box, not click-to-pick.
// This component renders a 4-per-row image grid plus a drop zone underneath
// in the same shadow DOM and emits "pick" with detail = { mmuId } when a tile
// is released over the drop zone — same shape as <kfe-tile-grid>'s pick event,
// so the host screen's _handlePick path is unchanged.
//
// Used by BOTH L2B Training and L2B Testing — iOS/Unity reuse the same drag
// layer for both modes; they differ only in feedback timing and scoring.
//
// Why Pointer Events instead of HTML5 drag:
//   HTML5 dragstart/drop don't fire on touch on iOS Safari without polyfill.
//   pointerdown/move/up + setPointerCapture works uniformly across mouse,
//   touch, and pen.
//
// Drag policy mirrors iOS:
//   • One drag at a time. While a tile is being dragged, the others freeze.
//   • Drop inside target rect → emit "pick"; the screen will re-render with
//     fresh tiles so we don't bother snapping back.
//   • Drop outside → snap back to the grid slot via CSS transition.
//   • pointercancel (system gesture, etc.) → snap back.
//
// Disabled state:
//   The host passes `disabled` during the FEEDBACK_MS feedback window so a
//   second drop can't fire while the ✓/✗ overlay is showing. Mirrors the
//   `if (this._feedbackTimer) return;` guard in the screen's _handlePick.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { resolveImage } from "../data/media-paths.js";

// Tap-vs-drag discrimination (Brief 051). iOS lets a UITapGestureRecognizer and
// a UIPanGestureRecognizer coexist on each tile; the pan only kicks in once the
// finger actually moves. We mirror that: a press that stays within TAP_SLOP_PX
// and is released within TAP_MAX_MS is a tap (→ enlarge preview); any movement
// past the slop commits to a drag.
const TAP_SLOP_PX = 10;
const TAP_MAX_MS = 300;

export class KFEL2BDragArea extends KFEComponent {
  // `urls` (Brief 134): an optional host-supplied { mmuId → imageUrl } map. When
  // the L2B Training screen pre-resolves + preloads all 8 tile images before
  // swapping the visible frame (atomic tile renewal), it passes the map in so
  // this component renders images on its FIRST paint instead of resolving them
  // asynchronously in onConnected — which left a blank-tile window that signals
  // the answer. Callers that don't pre-resolve (e.g. L2B Testing) omit it and
  // fall back to the self-resolution loop below.
  static props = ["items", "disabled", "urls"];
  static styles = `
    :host { display: block; }
    /* ALWAYS 4 per row across viewports — matches iOS canon (Rule #9). A prior
       responsive media query collapsed the grid to 3 columns at viewport widths
       ≤ 600px (mobile), producing a 3+3+2 layout on phones; Chris flagged this
       on his 2026-06-28 iPhone walk (L2B canon is 4×2 always; mirrored to
       <kfe-tile-grid> the same day). Tiles shrink to fit four columns on narrow
       viewports rather than reflow. */
    .grid { display: grid; gap: 0.5rem;
            grid-template-columns: repeat(4, 1fr); }
    /* White tile background — L2B images are transparent-PNG line drawings
       designed for white-paper rendering. Routed through the shared
       --kfe-l2b-image-bg token (Brief 061). Same rationale as kfe-tile-grid. */
    .tile { aspect-ratio: 1 / 1; background: var(--kfe-l2b-image-bg);
            border: 1px solid var(--kfe-border); border-radius: 8px;
            display: flex; align-items: center; justify-content: center;
            overflow: hidden; padding: 0; position: relative;
            cursor: grab;
            /* touch-action:none stops iOS scroll-on-drag from stealing the
               pointer stream the moment the finger moves. */
            touch-action: none;
            -webkit-user-select: none; user-select: none;
            -webkit-tap-highlight-color: transparent; }
    .tile.no-image { font-size: 0.85rem; padding: 0.5rem; text-align: center; }
    .tile img { width: 100%; height: 100%; object-fit: contain;
                pointer-events: none; -webkit-user-drag: none; }
    .tile.dragging { cursor: grabbing; z-index: 10;
                     box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4); }
    .tile.snapback { transition: transform 0.3s ease-out; }
    .tile.frozen { opacity: 0.45; }

    /* Drop zone: square, sized smaller than the grid, distinctly lighter
       than the page bg, and pushed well below the grid so it reads as
       its own discrete drop target. Matches the iOS L2B canon's
       central-target appearance. */
    .target-zone {
      margin: 3rem auto 0;
      /* Brief 135b: size relative to the (now bounded) exercise container, not
         the viewport. The old clamp()'s 25vw mid-stop tracked the window, so on
         a wide desktop or browser-zoom the drop target grew/shrank out of step
         with the container-relative tile grid. A percentage of the container
         keeps target and grid in proportion; the px clamps stop it collapsing
         on a narrow phone or ballooning past the iOS-canon square. */
      width: clamp(180px, 45%, 280px);
      aspect-ratio: 1 / 1;
      padding: 1rem; box-sizing: border-box;
      border: 2px dashed var(--kfe-muted);
      border-radius: 12px;
      background: color-mix(in srgb, var(--kfe-fg) 35%, var(--kfe-bg));
      display: flex; align-items: center; justify-content: center;
      color: var(--kfe-fg); text-align: center; font-size: 0.95rem;
      transition: background 0.15s ease, border-color 0.15s ease,
                  color 0.15s ease;
    }
    .target-zone.hover {
      border-style: solid; border-color: var(--kfe-accent);
      background: color-mix(in srgb, var(--kfe-accent) 15%, transparent);
      color: var(--kfe-fg);
    }

    /* Tap-to-enlarge preview (Brief 051). Mirrors iOS KFEContentsView
       touchImage/hideBigImageView: a single tap floats the tile image up to a
       full-bleed WHITE backdrop (UIColor whiteColor) sized to a
       shorter-viewport-side square; tapping anywhere dismisses it. The web
       port approximates iOS's 0.3s frame-morph with an opacity fade +
       scale-up — frame-exact FLIP is out of scope per the brief. The enter
       animation is driven declaratively via @starting-style so the RESTING
       state is the visible one (opacity 1); a throttled/background tab that
       never runs the transition still shows the preview rather than a blank
       white screen (an earlier rAF-gated version went invisible when rAF was
       frozen). Backdrop + image cell both use the canonical white L2B image
       bg so the line drawing reads as iOS intends. */
    .preview-overlay {
      position: fixed; inset: 0; z-index: 1000;
      display: flex; align-items: center; justify-content: center;
      background: var(--kfe-l2b-image-bg);
      opacity: 1; transition: opacity 0.3s ease;
      cursor: zoom-out; touch-action: manipulation;
      -webkit-tap-highlight-color: transparent; outline: none;
    }
    .preview-overlay .preview-image {
      width: min(100vw, 100vh); height: min(100vw, 100vh);
      max-width: 100%; max-height: 100%;
      object-fit: contain; background: var(--kfe-l2b-image-bg);
      transform: scale(1); transition: transform 0.3s ease;
      -webkit-user-select: none; user-select: none;
      -webkit-user-drag: none; pointer-events: none;
    }
    .preview-overlay .preview-label {
      font-size: 1.25rem; text-align: center; color: var(--kfe-fg);
      padding: 1rem;
    }
    @starting-style {
      .preview-overlay { opacity: 0; }
      .preview-overlay .preview-image { transform: scale(0.92); }
    }
  `;

  constructor() {
    super();
    /** @type {Record<number,string>|undefined} */ this._urls = undefined;
    /** @type {{ tile: HTMLElement, pointerId: number, startX: number, startY: number, startTime: number, mmuId: number, mmu: any, committed: boolean } | null} */
    this._activeDrag = null;
    /** @type {DOMRect | null} */ this._cachedTargetRect = null;
    /** Tile MMU currently shown enlarged in the preview overlay, or null. */
    /** @type {any|null} */ this._previewMmu = null;
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onPointerCancel = this._onPointerCancel.bind(this);
    this._onLostPointerCapture = this._onLostPointerCapture.bind(this);
  }

  async onConnected() {
    const self = /** @type {any} */ (this);
    if (!self.items) return;
    // Brief 134: if the host pre-resolved the tile URLs and passed them via the
    // `urls` prop, render() already reads them on first paint — skip the async
    // self-resolution loop entirely (it would only re-do work and re-render).
    if (self.urls && Object.keys(self.urls).length) return;
    this._urls = /** @type {Record<number,string>} */ ({});
    for (const m of self.items) {
      const url = await resolveImage(m);
      if (url) this._urls[m.mmuId] = url;
    }
    this._scheduleRender();
  }

  /** Resolved tile URL map — host-supplied (`urls` prop) wins over the
   *  component's own self-resolved cache. Keyed by mmuId. (Brief 134)
   *  @returns {Record<number,string>|undefined} */
  _resolvedUrls() {
    const injected = /** @type {any} */ (this).urls;
    if (injected && Object.keys(injected).length) return injected;
    return this._urls;
  }

  onDisconnected() {
    // Mid-drag teardown (component removed by host re-render). Skip the
    // snap-back animation — there's nothing to animate to.
    this._endDrag(/* dropped */ false, /* skipSnapback */ true);
  }

  /**
   * @param {PointerEvent} e
   * @param {HTMLElement} tile
   * @param {any} mmu
   */
  _onPointerDown(e, tile, mmu) {
    if (this._activeDrag) return;
    if (this._previewMmu) return; // ignore tile presses while a preview is up
    if (/** @type {any} */ (this).disabled) return;
    // Primary button only on mouse; touch and pen always report button = 0.
    if (!e.isPrimary || e.button !== 0) return;

    e.preventDefault();
    try { tile.setPointerCapture(e.pointerId); } catch { /* not supported */ }

    // Cache target rect once at press start. The tile uses translate() which
    // doesn't reflow neighbours, so the target rect is stable until release.
    const targetEl = this.shadowRoot?.querySelector(".target-zone") ?? null;
    this._cachedTargetRect = targetEl ? targetEl.getBoundingClientRect() : null;

    // Don't commit to a drag yet — a press might still resolve to a tap (→
    // preview). The freeze/dragging visuals are deferred to _commitDrag(),
    // which the first past-slop pointermove triggers.
    this._activeDrag = {
      tile,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startTime: performance.now(),
      mmuId: mmu.mmuId,
      mmu,
      committed: false,
    };

    // Brief 135a: bind move/up/cancel at the WINDOW, not the tile. With pointer
    // capture a release normally re-targets the tile, but a release outside the
    // viewport (drag a tile off the window edge, let go past the browser chrome)
    // can be dropped — the tile would then be stuck mid-drag and un-re-draggable.
    // Window-level listeners catch the release wherever it lands, and the
    // `lostpointercapture` backstop snaps the tile home if the browser silently
    // revokes capture (system gesture, pointer leaves the surface, etc.).
    window.addEventListener("pointermove", this._onPointerMove);
    window.addEventListener("pointerup", this._onPointerUp);
    window.addEventListener("pointercancel", this._onPointerCancel);
    tile.addEventListener("lostpointercapture", this._onLostPointerCapture);
  }

  /** Promote the current press to an active drag (mirrors iOS pan kicking in). */
  _commitDrag() {
    if (!this._activeDrag || this._activeDrag.committed) return;
    const { tile } = this._activeDrag;
    // Single-drag policy: freeze the rest. Mirrors iOS setDragViewIsCanDrag.
    this.shadowRoot?.querySelectorAll(".tile").forEach((node) => {
      if (node !== tile) node.classList.add("frozen");
    });
    tile.classList.remove("snapback");
    tile.classList.add("dragging");
    this._activeDrag.committed = true;
  }

  /** @param {PointerEvent} e */
  _onPointerMove(e) {
    if (!this._activeDrag || e.pointerId !== this._activeDrag.pointerId) return;
    const { tile, startX, startY } = this._activeDrag;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (!this._activeDrag.committed) {
      if (Math.hypot(dx, dy) < TAP_SLOP_PX) return; // still inside tap slop
      this._commitDrag();
    }
    tile.style.transform = `translate(${dx}px, ${dy}px)`;

    const targetEl = this.shadowRoot?.querySelector(".target-zone");
    if (targetEl && this._cachedTargetRect) {
      const r = this._cachedTargetRect;
      const inside =
        e.clientX >= r.left && e.clientX <= r.right &&
        e.clientY >= r.top  && e.clientY <= r.bottom;
      targetEl.classList.toggle("hover", inside);
    }
  }

  /** @param {PointerEvent} e */
  _onPointerUp(e) {
    if (!this._activeDrag || e.pointerId !== this._activeDrag.pointerId) return;
    const drag = this._activeDrag;
    const moved = Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) >= TAP_SLOP_PX;

    if (!drag.committed && !moved) {
      // Short, stationary press → a tap. Toggle the enlarge preview. Nothing
      // moved visually, so there's no snap-back to animate.
      const quick = performance.now() - drag.startTime < TAP_MAX_MS;
      const mmu = drag.mmu;
      this._endDrag(/* dropped */ false, /* skipSnapback */ true);
      if (quick) this._togglePreview(mmu);
      return;
    }

    // Drag release: drop only counts if released inside the target rect.
    let dropped = false;
    if (this._cachedTargetRect) {
      const r = this._cachedTargetRect;
      dropped =
        e.clientX >= r.left && e.clientX <= r.right &&
        e.clientY >= r.top  && e.clientY <= r.bottom;
    }
    const mmuId = drag.mmuId;
    this._endDrag(dropped, /* skipSnapback */ !drag.committed);
    if (dropped) this.emit("pick", { mmuId });
  }

  /** @param {PointerEvent} e */
  _onPointerCancel(e) {
    if (!this._activeDrag || e.pointerId !== this._activeDrag.pointerId) return;
    this._endDrag(false, /* skipSnapback */ !this._activeDrag.committed);
  }

  /**
   * Browser revoked the implicit pointer capture mid-drag (pointer left the
   * surface / a system gesture stole it) without a pointerup/pointercancel
   * reaching us. Treat it as a release outside the target → snap the tile home
   * so it stays re-draggable (Brief 135a). Guarded on `_activeDrag` so the
   * lostpointercapture our own `releasePointerCapture` queues in `_endDrag`
   * (after the listener is already removed) is a no-op.
   * @param {PointerEvent} e
   */
  _onLostPointerCapture(e) {
    if (!this._activeDrag || e.pointerId !== this._activeDrag.pointerId) return;
    this._endDrag(false, /* skipSnapback */ !this._activeDrag.committed);
  }

  /**
   * @param {boolean} dropped
   * @param {boolean} [skipSnapback]
   */
  _endDrag(dropped, skipSnapback = false) {
    if (!this._activeDrag) return;
    const { tile, pointerId } = this._activeDrag;

    try { tile.releasePointerCapture(pointerId); } catch { /* not captured */ }
    window.removeEventListener("pointermove", this._onPointerMove);
    window.removeEventListener("pointerup", this._onPointerUp);
    window.removeEventListener("pointercancel", this._onPointerCancel);
    tile.removeEventListener("lostpointercapture", this._onLostPointerCapture);
    tile.classList.remove("dragging");

    if (dropped || skipSnapback) {
      // On a successful drop the host re-renders with new tiles — there's no
      // visible tile to animate. On disconnect there's nothing left to do.
      tile.style.transform = "";
    } else {
      // Snap back via CSS transition.
      tile.classList.add("snapback");
      tile.style.transform = "";
      const cleanup = () => {
        tile.classList.remove("snapback");
        tile.removeEventListener("transitionend", cleanup);
      };
      tile.addEventListener("transitionend", cleanup);
    }

    const targetEl = this.shadowRoot?.querySelector(".target-zone");
    targetEl?.classList.remove("hover");
    this.shadowRoot?.querySelectorAll(".tile").forEach((node) => {
      node.classList.remove("frozen");
    });
    this._cachedTargetRect = null;
    this._activeDrag = null;
  }

  // Tap-to-enlarge preview (Brief 051). The drag-area owns this purely as a
  // view affordance — it never touches engine state, so the host screen's
  // target/score/answered state is untouched while a preview is up. Mirrors
  // iOS KFEContentsView, where touchImage only floats a copy of the image and
  // hideBigImageView tears it back down; the underlying exercise is unchanged.
  /** @param {any} mmu */
  _togglePreview(mmu) {
    if (this._previewMmu && this._previewMmu.mmuId === mmu.mmuId) {
      this._dismissPreview();
      return;
    }
    this._previewMmu = mmu;
    this.emit("kfe:tile-preview", { mmuId: mmu.mmuId });
    this._scheduleRender();
    // Pull focus to the overlay once it renders, for keyboard / VoiceOver
    // dismissal. setTimeout (not rAF) so it fires even when the tab is
    // throttled and rAF is frozen.
    setTimeout(() => {
      const ov = /** @type {HTMLElement|null} */ (
        this.shadowRoot?.querySelector(".preview-overlay") ?? null
      );
      ov?.focus();
    }, 0);
  }

  _dismissPreview() {
    if (!this._previewMmu) return;
    const mmuId = this._previewMmu.mmuId;
    this._previewMmu = null;
    this.emit("kfe:tile-preview-dismiss", { mmuId });
    this._scheduleRender();
  }

  /** @param {any} mmu @returns {HTMLElement} */
  _buildPreviewOverlay(mmu) {
    const overlay = document.createElement("div");
    overlay.className = "preview-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", t("l2b.imagePreview", { phrase: mmu.phrase }));
    overlay.tabIndex = 0;

    const url = this._resolvedUrls()?.[mmu.mmuId];
    if (url) {
      const img = document.createElement("img");
      img.src = url;
      img.alt = mmu.phrase;
      img.className = "preview-image l2b-image-container";
      img.draggable = false;
      overlay.appendChild(img);
    } else {
      const label = document.createElement("div");
      label.className = "preview-label";
      label.textContent = mmu.phrase;
      overlay.appendChild(label);
    }

    // Tap anywhere on the overlay (iOS: backgroundView tap → hideBigImageView).
    overlay.addEventListener("click", () => this._dismissPreview());
    // Keyboard / VoiceOver dismissal — Escape, or activating the focused dialog.
    overlay.addEventListener("keydown", (e) => {
      const k = /** @type {KeyboardEvent} */ (e).key;
      if (k === "Escape" || k === "Enter" || k === " ") {
        e.preventDefault();
        this._dismissPreview();
      }
    });

    return overlay;
  }

  render() {
    const items = /** @type {any[]} */ (/** @type {any} */ (this).items) ?? [];

    const urls = this._resolvedUrls();
    const grid = document.createElement("div");
    grid.className = "grid";
    for (const m of items) {
      const tile = document.createElement("div");
      tile.className = "tile l2b-image-container" + (urls?.[m.mmuId] ? "" : " no-image");
      tile.setAttribute("role", "button");
      tile.setAttribute("aria-label", m.phrase);
      if (urls?.[m.mmuId]) {
        const img = document.createElement("img");
        img.src = urls[m.mmuId];
        img.alt = m.phrase;
        img.draggable = false;
        img.loading = "lazy";
        tile.appendChild(img);
      } else {
        tile.textContent = m.phrase;
      }
      tile.addEventListener("pointerdown", (e) => {
        this._onPointerDown(/** @type {PointerEvent} */ (e), tile, m);
      });
      grid.appendChild(tile);
    }

    const targetZone = document.createElement("div");
    targetZone.className = "target-zone";
    targetZone.textContent = t("l2b.dropHere");

    const overlay = this._previewMmu
      ? this._buildPreviewOverlay(this._previewMmu)
      : document.createDocumentFragment();

    return html`${grid}${targetZone}${overlay}`;
  }
}
